import {
  Box,
  Card,
  CardActionArea,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { motion } from 'motion/react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffectOnce, useTimeoutFn } from 'react-use';

import { OrderModel } from '@merchant-clients/order';

import { GuestIcon, InfoCircleIcon } from '@frontend/components';
import { useUpdateOrderMutation } from '@frontend/data';
import {
  OrderCardSizeVariantType,
  usePersistentUiSettings,
} from '@frontend/utils';

import {
  getOrderGuestDisplayLabel,
  hasOrderGuestDisplayInfo,
} from './order-guest-display.utils';
import { OrderInfo } from './order-info.component';
import { statusVariantMap } from './order-list.component';
import { orderNextStatusMap } from './order-status-transition.utils';

export interface SizeSettingsModel {
  item: {
    fontSizeMain: string;
    fontSizeOptions: string;
  };
  card: {
    fontSize: string;
    width: string;
    icon: number;
  };
}

export const sizeSettingsMap: Record<
  OrderCardSizeVariantType,
  SizeSettingsModel
> = {
  small: {
    item: {
      fontSizeMain: '16px',
      fontSizeOptions: '12px',
    },
    card: {
      fontSize: '14px',
      width: '250px',
      icon: 20,
    },
  },
  medium: {
    item: {
      fontSizeMain: '20px',
      fontSizeOptions: '14px',
    },
    card: {
      fontSize: '18px',
      width: '317px',
      icon: 24,
    },
  },
  large: {
    item: {
      fontSizeMain: '28px',
      fontSizeOptions: '16px',
    },
    card: {
      fontSize: '22px',
      width: '428px',
      icon: 28,
    },
  },
};

export interface OrderListItemProps {
  order: OrderModel;
  variant?: 'neutral' | 'warning' | 'success' | 'error';
}

export const OrderListItem = ({ order, variant }: OrderListItemProps) => {
  const theme = useTheme();
  const { shopId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderIdParam = searchParams.get('orderId');
  const isOrderSelected = orderIdParam === order.id;
  const orderStatusMutation = useUpdateOrderMutation();
  const {
    ordersPage: { orderCardSettings },
  } = usePersistentUiSettings();

  const [_isReady, cancelSingleClick, resetSingleClickTimer] = useTimeoutFn(
    () => {
      if (orderIdParam) {
        searchParams.delete('orderId');
      } else {
        searchParams.set('orderId', order.id);
      }

      setSearchParams(searchParams, { replace: true });
    },
    250,
  );

  useEffectOnce(() => {
    // First time we render cancel the timeout so it doesn't trigger
    // without a click
    cancelSingleClick();
  });

  const handleSingleClick = () => {
    resetSingleClickTimer();
  };

  const handleDoubleClick = () => {
    cancelSingleClick();

    if (
      order.loadingState ||
      !orderNextStatusMap.has(order.status) ||
      !shopId
    ) {
      return;
    }

    const nextStatus = orderNextStatusMap.get(order.status)!;

    orderStatusMutation.mutate({
      shopId,
      orderId: order.id,
      status: nextStatus,
    });
  };

  return (
    <>
      <motion.li
        initial={{ '--angle': '1deg' }}
        animate={order.loadingState ? { '--angle': '360deg' } : undefined}
        transition={{
          duration: 2,
          repeat: Number.POSITIVE_INFINITY,
          ease: 'linear',
        }}
        style={{
          width: '100%',
          maxWidth: sizeSettingsMap[orderCardSettings.cardSize].card.width,
          position: 'relative',
          display: 'inline-flex',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            width: '100%',
            zIndex: isOrderSelected ? theme.zIndex.drawer + 1 : '1',
            padding: 0.5,
            '::after, ::before': order.loadingState
              ? {
                  content: '""',
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                  backgroundImage:
                    order.status === 'CONFIRMED'
                      ? 'conic-gradient(from var(--angle), transparent 70%, rgba(186, 249, 191, 1))'
                      : order.status === 'READY'
                        ? 'conic-gradient(from var(--angle), transparent 70%, rgb(255,214,214))'
                        : order.status === 'PENDING'
                          ? 'conic-gradient(from var(--angle), transparent 70%,#fceba8ff)'
                          : 'conic-gradient(from var(--angle), transparent 70%, rgba(164, 164, 163, 1))',
                  top: '50%',
                  left: '50%',
                  translate: '-50% -50%',
                  zIndex: -1,
                  borderRadius: '12px',
                }
              : {},
            '::before': order.loadingState
              ? {
                  filter: 'blur(1.5rem)',
                  opacity: '0.5',
                }
              : {},
          }}
        >
          <Card
            sx={{
              width: '100%',
              border: isOrderSelected
                ? `1px solid ${theme.palette.text.primary}`
                : undefined,
              borderRadius: '12px',
            }}
          >
            <CardActionArea
              sx={{ wordBreak: 'break-word' }}
              onClick={handleSingleClick}
              onDoubleClick={handleDoubleClick}
            >
              <Stack
                sx={{
                  p: 2,
                  backgroundColor:
                    theme.palette[variant ?? statusVariantMap[order.status]]
                      .light,
                }}
              >
                <OrderInfo
                  order={order}
                  variant={variant ?? statusVariantMap[order.status]}
                  hideTimeParameters={!orderCardSettings.showTimeParameters}
                />
              </Stack>

              {(orderCardSettings.showItems ||
                (orderCardSettings.showGuestName &&
                  hasOrderGuestDisplayInfo(
                    order.guestName,
                    order.guestEmail,
                  )) ||
                order.message) && (
                <Stack
                  sx={{
                    p: 2,
                    pt: 1.5,
                    backgroundColor: theme.palette.background.paper,
                  }}
                  spacing={1}
                >
                  {hasOrderGuestDisplayInfo(
                    order.guestName,
                    order.guestEmail,
                  ) &&
                    orderCardSettings.showGuestName && (
                      <>
                        <Stack spacing={0.5} direction="row">
                          <GuestIcon
                            width={
                              sizeSettingsMap[orderCardSettings.cardSize].card
                                .icon
                            }
                            height={
                              sizeSettingsMap[orderCardSettings.cardSize].card
                                .icon
                            }
                          />
                          <Typography
                            variant="body2"
                            fontSize={
                              sizeSettingsMap[orderCardSettings.cardSize].card
                                .fontSize
                            }
                          >
                            {getOrderGuestDisplayLabel(
                              order.guestName,
                              order.guestEmail,
                            )}
                          </Typography>
                        </Stack>
                        {(orderCardSettings.showItems || order.message) && (
                          <Divider />
                        )}
                      </>
                    )}

                  {order.message && (
                    <>
                      <Stack
                        spacing={0.5}
                        alignItems="start"
                        direction="row"
                        pt={0.5}
                      >
                        <Box
                          height="100%"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <InfoCircleIcon
                            width={
                              sizeSettingsMap[orderCardSettings.cardSize].card
                                .icon
                            }
                            height={
                              sizeSettingsMap[orderCardSettings.cardSize].card
                                .icon
                            }
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          fontSize={
                            sizeSettingsMap[orderCardSettings.cardSize].card
                              .fontSize
                          }
                        >
                          {order.message}
                        </Typography>
                      </Stack>
                      {orderCardSettings.showItems && <Divider />}
                    </>
                  )}

                  {orderCardSettings.showItems && (
                    <List disablePadding>
                      {order.items.map((item, index) => (
                        <ListItem
                          key={`item-${item.id}-${index}`}
                          disablePadding
                          sx={{
                            minHeight: 0,
                            flexDirection: 'column',
                          }}
                        >
                          <Stack direction="row" spacing={0.5} width="100%">
                            <Typography
                              variant="body2"
                              sx={{ minWidth: theme.spacing(2) }}
                              fontSize={
                                sizeSettingsMap[orderCardSettings.itemFontSize]
                                  .item.fontSizeMain
                              }
                            >
                              {item.quantity}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontSize={
                                sizeSettingsMap[orderCardSettings.itemFontSize]
                                  .item.fontSizeMain
                              }
                            >
                              {item.name}
                            </Typography>
                          </Stack>

                          {item.options && item.options.length > 0 && (
                            <List
                              disablePadding
                              sx={{ mb: 0.5, width: '100%' }}
                            >
                              {item.options.map((option) => (
                                <ListItem
                                  key={`option-${option.id}`}
                                  disablePadding
                                  sx={{ minHeight: 0, marginLeft: 2 }}
                                >
                                  <Stack direction="row" spacing={0.5}>
                                    <Typography
                                      variant="caption"
                                      component="p"
                                      sx={{ minWidth: theme.spacing(2) }}
                                      fontSize={
                                        sizeSettingsMap[
                                          orderCardSettings.itemFontSize
                                        ].item.fontSizeOptions
                                      }
                                    >
                                      {option.quantity}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      component="p"
                                      fontSize={
                                        sizeSettingsMap[
                                          orderCardSettings.itemFontSize
                                        ].item.fontSizeOptions
                                      }
                                    >
                                      {option.name}
                                    </Typography>
                                  </Stack>
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Stack>
              )}
            </CardActionArea>
          </Card>
        </Box>
      </motion.li>
    </>
  );
};
